import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { gsap } from 'gsap';
import { Search } from 'lucide-react';
import F1Tile from '../F1Tile';
import MusicPlayer from '../MusicPlayer';
import MangaTile from '../MangaTile';
import AnimeTile from '../AnimeTile';
import TravelMap from '../TravelMap';
import YouTubeTile from '../YouTubeTile';
import VibeCodeBanner from '../VibeCodeBanner';
// Base functional layout (grid columns/spans, widget sizing) for the bento
// widgets below is defined here — reused as-is since the widgets themselves
// depend on it. AboutV2.css layers a completely different visual skin on
// top, scoped under .about-v2 so none of it leaks onto the original /about
// page that still uses About.tsx directly.
import '../About.css';
import './AboutV2.css';

interface PhotoMetadata {
  src: string;
  camera?: string;
  cameraImage?: string;
  lens?: string;
  iso?: string;
  aperture?: string;
  shutter?: string;
  focalLength?: string;
  location?: string;
}

const AboutV2: React.FC = () => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoMetadata | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const photographs: PhotoMetadata[] = [
    { src: '/content/photographs/DSC00238.jpg', camera: 'Sony DSLR-A200', cameraImage: '/content/cameras/sony-a200.png', iso: '100', aperture: 'f/1.0', shutter: '1/1000' },
    { src: '/content/photographs/DSC00291.jpg', camera: 'Sony DSLR-A200', cameraImage: '/content/cameras/sony-a200.png', lens: '50mm F1.7', iso: '800', aperture: 'f/5.6', shutter: '1/100', focalLength: '50mm' },
    { src: '/content/photographs/DSC00304.jpg', camera: 'Sony DSLR-A200', cameraImage: '/content/cameras/sony-a200.png', iso: '800', aperture: 'f/1.0', shutter: '1/30' },
    { src: '/content/photographs/DSC00619.jpg', camera: 'Sony DSLR-A200', cameraImage: '/content/cameras/sony-a200.png', iso: '800', aperture: 'f/1.0', shutter: '1/80' },
    { src: '/content/photographs/DSCF4823.JPG', camera: 'Fujifilm X-E3', cameraImage: '/content/cameras/fujifilm-xe3.png', iso: '160', aperture: 'f/2.8', shutter: '1/250' },
    { src: '/content/photographs/DSCF4850.JPG', camera: 'Fujifilm X-E3', cameraImage: '/content/cameras/fujifilm-xe3.png', iso: '160', aperture: 'f/2.8', shutter: '1/250' },
    { src: '/content/photographs/DSCF4865.JPG', camera: 'Fujifilm X-E3', cameraImage: '/content/cameras/fujifilm-xe3.png', iso: '160', aperture: 'f/2.8', shutter: '1/500' },
    { src: '/content/photographs/DSCF5045.JPG', camera: 'Fujifilm X-E3', cameraImage: '/content/cameras/fujifilm-xe3.png', iso: '160', aperture: 'f/4.0', shutter: '1/320' },
    { src: '/content/photographs/DSCF5055.JPG', camera: 'Fujifilm X-E3', cameraImage: '/content/cameras/fujifilm-xe3.png', iso: '160', aperture: 'f/4.0', shutter: '1/250' },
    { src: '/content/photographs/DSCF5133.JPG', camera: 'Fujifilm X-E3', cameraImage: '/content/cameras/fujifilm-xe3.png', iso: '320', aperture: 'f/2.8', shutter: '1/125' },
    { src: '/content/photographs/IMG_2877-2.jpg', camera: 'Canon EOS 7D', cameraImage: '/content/cameras/canon-eos7d.png', lens: 'EF50mm f/1.8 STM', iso: '125', aperture: 'f/1.8', shutter: '1/320', focalLength: '50mm' },
    { src: '/content/photographs/IMG_2889-2.jpg', camera: 'Canon EOS 7D', cameraImage: '/content/cameras/canon-eos7d.png', lens: 'EF50mm f/1.8 STM', iso: '100', aperture: 'f/1.8', shutter: '1/320', focalLength: '50mm' },
    { src: '/content/photographs/IMG_2936.jpg', camera: 'Canon EOS 7D', cameraImage: '/content/cameras/canon-eos7d.png', lens: 'EF50mm f/1.8 STM', iso: '125', aperture: 'f/2.0', shutter: '1/60', focalLength: '50mm' },
    { src: '/content/photographs/_DSC1284.jpg', camera: 'Sony DSLR-A200', cameraImage: '/content/cameras/sony-a200.png', iso: '100', aperture: 'f/1.0', shutter: '1/2000' },
    { src: '/content/photographs/_DSC1375.jpg', camera: 'Sony DSLR-A200', cameraImage: '/content/cameras/sony-a200.png', iso: '100', aperture: 'f/1.0', shutter: '1/2000' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => { if (entry.isIntersecting) setIsMapVisible(true); }),
      { threshold: 0.3 }
    );
    if (mapContainerRef.current) observer.observe(mapContainerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedPhoto) setSelectedPhoto(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhoto]);

  useEffect(() => {
    document.body.style.overflow = selectedPhoto ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [selectedPhoto]);

  // Whole page slides in from the right, same as work/publications, once
  // the panel itself has settled — same 650ms panel-settle delay
  // convention as the other HomeV2 views, so this doesn't visually fight
  // the panel's own slide-in.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    gsap.set(el, { opacity: 0, x: 80 });
    const timer = setTimeout(() => {
      gsap.to(el, { opacity: 1, x: 0, duration: 0.55, ease: 'power3.out' });
    }, 650);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="about-v2" ref={rootRef}>
      {/* ── Character card ── */}
      <section className="av2-hero">
        <div className="av2-hero-frame">
          <div className="av2-portrait">
            <img src="/content/About/about main.jpg" alt="Aditya Naik" />
          </div>

          <div className="av2-hero-info">
            <h1 className="av2-name">Aditya Naik</h1>
            <p className="av2-pronunciation">
              pronounced <strong>aah-dit-yuh</strong> &middot; friends call me Adi (aah-di)
            </p>
            <p className="av2-bio">
              Welcome to my little corner of the internet! I'm an HCI grad student at Indiana University.
              Outside of design and research, you'll find me hunting for empty equipment at the gym, redeeming
              Panda Express entrées, and listening to wacky synth progressions.
            </p>

            <div className="av2-region">
              <p className="av2-region-text">I come from a small coastal region called Goa 🌴</p>
              <div ref={mapContainerRef} className={`av2-map ${isMapVisible ? 'is-visible' : ''}`}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d988276.0566218346!2d73.31909!3d15.2993!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bbfba106336b741%3A0xeaf887ff62f34092!2sGoa!5e0!3m2!1sen!2sin!4v1635000000000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Map of Goa, India"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── My Story ── */}
      <section className="av2-lore">
        <div className="av2-story-card">
          <div className="av2-lore-media">
            <video
              autoPlay
              loop
              muted
              playsInline
              onTimeUpdate={(e) => {
                const video = e.currentTarget;
                if (video.duration - video.currentTime < 1) video.currentTime = 0;
              }}
            >
              <source src="/content/About/IMG_7676.MOV" type="video/quicktime" />
              <source src="/content/About/IMG_7676.MOV" type="video/mp4" />
            </video>
          </div>
          <div className="av2-story-text">
            <p>
              Growing up, I was that kid who would take things apart just to see what was inside. New RC car?
              Give it a day, and I'd have it completely disassembled on the floor. Why? Because I wondered how it
              worked. My family affectionately called me their 'तोड़ फोड़ स्पेशलिस्ट' (tod fod specialist, or
              'break-and-fix expert'). I enrolled in Information Technology engineering and genuinely enjoyed
              coding. But somewhere between writing functions and debugging loops, something shifted. My focus
              moved from perfecting the code to perfecting the <em>experience</em>. The interface, the usability,
              how people actually felt using what I built. That's where my heart was. I'd stumbled into my
              calling without even realizing I was looking for it.
            </p>
          </div>
        </div>
      </section>

      {/* ── Fragments (the bento grid, same widgets, brutalist skin) ── */}
      <section className="av2-inventory">
        <div className="bento-grid av2-bento-grid">
          <F1Tile />
          <MusicPlayer />

          <div className="bento-item bento-photography">
            <div className="widget-content photography-widget">
              <div className="photography-header-section">
                <h3 className="photography-title">Through My Lens</h3>
              </div>
              <div className="photo-grid">
                {photographs.map((photo, index) => (
                  <div key={index} className="photo-item" onClick={() => setSelectedPhoto(photo)}>
                    <img src={photo.src} alt={`Photography ${index + 1}`} className="photo-image" loading="lazy" />
                    <div className="photo-overlay">
                      <Search className="photo-zoom-icon" size={32} strokeWidth={2} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {selectedPhoto && ReactDOM.createPortal(
            <div className="lightbox av2-lightbox" onClick={() => setSelectedPhoto(null)}>
              <div className="lightbox-layout" onClick={(e) => e.stopPropagation()}>
                <button className="lightbox-close" onClick={() => setSelectedPhoto(null)}>✕</button>
                <div className="lightbox-image-container">
                  <img src={selectedPhoto.src} alt="Selected photograph" className="lightbox-image" />
                </div>
                <div className="lightbox-metadata-card">
                  <h3 className="metadata-title">Image Details</h3>
                  {selectedPhoto.camera && (
                    <div className="metadata-camera-section">
                      <span className="metadata-label">Camera</span>
                      <span className="metadata-value">{selectedPhoto.camera}</span>
                      {selectedPhoto.cameraImage && (
                        <img src={selectedPhoto.cameraImage} alt={selectedPhoto.camera} className="metadata-camera-image" />
                      )}
                    </div>
                  )}
                  {selectedPhoto.lens && (
                    <div className="metadata-row"><span className="metadata-label">Lens</span><span className="metadata-value">{selectedPhoto.lens}</span></div>
                  )}
                  {selectedPhoto.iso && (
                    <div className="metadata-row"><span className="metadata-label">ISO</span><span className="metadata-value">{selectedPhoto.iso}</span></div>
                  )}
                  {selectedPhoto.aperture && (
                    <div className="metadata-row"><span className="metadata-label">Aperture</span><span className="metadata-value">{selectedPhoto.aperture}</span></div>
                  )}
                  {selectedPhoto.shutter && (
                    <div className="metadata-row"><span className="metadata-label">Shutter Speed</span><span className="metadata-value">{selectedPhoto.shutter}</span></div>
                  )}
                  {selectedPhoto.focalLength && (
                    <div className="metadata-row"><span className="metadata-label">Focal Length</span><span className="metadata-value">{selectedPhoto.focalLength}</span></div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}

          <MangaTile />
          <AnimeTile />
          <TravelMap />
          <YouTubeTile />
          <VibeCodeBanner />
        </div>
      </section>
    </div>
  );
};

export default AboutV2;
